import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { registerAuthBridge } from '@/lib/api-client';

export type UserRole = 'admin' | 'manager' | 'driver' | 'workshop';

export interface AuthUser {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  role: UserRole;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  accessTokenExpiresAt: string | null;
  /** true enquanto a primeira checagem de sessão (refresh silencioso) roda. */
  bootstrapping: boolean;

  setSession: (payload: {
    user: AuthUser;
    accessToken: string;
    accessTokenExpiresAt: string;
  }) => void;
  setAccessToken: (token: string, expiresAt: string) => void;
  setBootstrapping: (value: boolean) => void;
  clear: () => void;
}

/**
 * Store de autenticação.
 *
 * Persistimos apenas o `user` (para mostrar nome/role enquanto o refresh
 * silencioso acontece). O accessToken vive só em memória — se o usuário
 * recarregar a página, o refresh cookie httpOnly é usado para obter um novo.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      accessTokenExpiresAt: null,
      bootstrapping: true,

      setSession: ({ user, accessToken, accessTokenExpiresAt }) =>
        set({ user, accessToken, accessTokenExpiresAt, bootstrapping: false }),

      setAccessToken: (accessToken, accessTokenExpiresAt) =>
        set({ accessToken, accessTokenExpiresAt }),

      setBootstrapping: (bootstrapping) => set({ bootstrapping }),

      clear: () =>
        set({
          user: null,
          accessToken: null,
          accessTokenExpiresAt: null,
          bootstrapping: false,
        }),
    }),
    {
      name: 'frota-auth',
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

/**
 * Conecta o store ao api-client (que não importa o store diretamente para
 * evitar ciclo). Precisa rodar uma vez no bootstrap do app.
 */
export function bindAuthBridge() {
  registerAuthBridge({
    getAccessToken: () => useAuthStore.getState().accessToken,
    setAccessToken: (token, expiresAt) =>
      useAuthStore.getState().setAccessToken(token, expiresAt),
    clear: () => useAuthStore.getState().clear(),
  });
}
